import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { FOUNDER_PHOTO } from "../data/content";
import { scrollToId } from "../lib/scroll";

const ROLES = ["Chief Executive Officer", "Chief Operating Officer", "Chief Translational Officer", "Clinical Research Leader", "Board Director & Strategic Advisor"];

export const Founder = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section id="founder" ref={ref} data-testid="founder-section" className="bg-[#070D1D] py-24 lg:py-36 relative overflow-hidden">
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] rounded-full bg-teal-500/[0.06] blur-[130px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative" data-testid="founder-photo-frame">
                <div className="absolute -inset-3 border border-teal-500/25 translate-x-4 translate-y-4 pointer-events-none" />
                <div className="relative overflow-hidden aspect-[4/5]">
                  <motion.img
                    style={{ y: imgY, scale: 1.12 }}
                    src={FOUNDER_PHOTO}
                    alt="Dr. Launa Aspeslet, PhD — Founder of Calliope"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                    data-testid="founder-photo"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#070D1D]/70 via-transparent to-[#070D1D]/10 pointer-events-none" />
                  <div className="absolute inset-0 bg-teal-900/10 mix-blend-multiply pointer-events-none" />
                </div>
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-teal-300">Founder & CEO</p>
                  <p className="font-serif text-2xl text-white mt-1">Dr. Launa Aspeslet, PhD</p>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <Eyebrow dark>Meet Dr. Launa Aspeslet</Eyebrow>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.12] text-[#F8F9FA] mt-6">
                An operator's judgment, earned in the <em className="italic text-teal-300">operating seat.</em>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 text-base sm:text-lg text-slate-300 leading-relaxed">
                Dr. Launa Aspeslet founded Calliope to connect the people, expertise and
                capabilities life sciences organizations need to move promising science forward.
                She is a senior executive, strategic advisor and board leader with nearly three
                decades of experience across biotechnology, pharmaceuticals and clinical research —
                spanning scientific development, clinical programs, regulatory pathways and
                organizational growth.
              </p>
              <p className="mt-5 text-base text-slate-400 leading-relaxed">
                She has guided products from early-stage research through clinical development and
                global regulatory review, led hundreds of health authority interactions across major
                markets, and scaled a clinical research organization to operations in more than 20
                countries with a team of over 300. Calliope brings that operator's perspective to
                founders, executives, investors and boards facing consequential decisions.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-10 flex flex-wrap gap-2.5" data-testid="founder-roles">
                {ROLES.map((r) => (
                  <span key={r} className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-300 border border-white/15 px-4 py-2 hover:border-teal-400/50 hover:text-teal-300 transition-colors duration-300">
                    {r}
                  </span>
                ))}
              </div>
              <button
                data-testid="founder-cta"
                onClick={() => scrollToId("contact")}
                className="mt-10 inline-flex items-center gap-2 text-teal-300 hover:text-teal-200 text-sm font-semibold tracking-wide transition-colors group"
              >
                Start a conversation with Dr. Aspeslet
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </button>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
